import type { FunctionReturnType } from "convex/server";
import type { api } from "@constructionautomation/backend";
import type {
  CountRow,
  TagRecord,
  MeasurementBreakdownTargetRef,
  MeasurementBreakdownTargetRow,
  LinkedSchedulePreviewSummary,
  ScheduleOverride,
  TakeoffLinkedSchedulePreviewResponse,
} from "@/types/takeoff-pilot";
import { formatMeasurementValue, roundMeasurementValue, type MeasurementType } from "@/lib/geometry";
import { MEASUREMENT_TYPES } from "@/lib/measurement-domain";
import { toCSV, downloadFile } from "@/lib/csv";
import { toCellRecord } from "@/lib/schedule-cells";
import {
  TAKEOFF_COST_BASIS,
  type TakeoffCostBasis,
} from "@/lib/takeoff-domain";

/**
 * Keys the linked schedule preview's cells by column. The action returns cell
 * lists because schedule headers can't be Convex field names.
 */
export function keyLinkedSchedulePreviewCells(
  response: FunctionReturnType<typeof api.takeoffPilot.tagPreview.getLinkedSchedulePreview>
): TakeoffLinkedSchedulePreviewResponse {
  return {
    availableColumns: response.availableColumns,
    linkedPreviews: response.linkedPreviews.map((preview) => ({
      ...preview,
      previewCells: toCellRecord(preview.previewCells),
      matchedRows: preview.matchedRows.map((row) => ({ ...row, cells: toCellRecord(row.cells) })),
    })),
  };
}

export const UNIT_COST_DECIMAL_PATTERN = /^\d+(\.\d{1,2})?$/;

/** Alphabetic then numeric tag order (P1, P2, P10, TA1), matching FieldPilot. */
const TAG_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function compareTagsByRawValue(
  a: { rawValue: string },
  b: { rawValue: string }
): number {
  return TAG_COLLATOR.compare(a.rawValue, b.rawValue);
}

export function getEffectiveCostBasis(
  costBasis?: TakeoffCostBasis
): TakeoffCostBasis {
  return costBasis ?? TAKEOFF_COST_BASIS.measurement;
}

export function getMeasurementQuantity(result?: Pick<CountRow, "lf" | "sqft" | "cf">): number {
  if (!result) return 0;
  if (result.lf > 0) return roundMeasurementValue(result.lf, MEASUREMENT_TYPES.LF);
  if (result.sqft > 0) return roundMeasurementValue(result.sqft, MEASUREMENT_TYPES.SQFT);
  if (result.cf > 0) return roundMeasurementValue(result.cf, MEASUREMENT_TYPES.CF);
  return 0;
}

export function getTotalCost(
  result: Pick<CountRow, "count" | "lf" | "sqft" | "cf"> | undefined,
  unitCost?: number | null,
  costBasis?: TakeoffCostBasis
): number | null {
  if (unitCost === null || unitCost === undefined) {
    return null;
  }
  const effectiveCostBasis = getEffectiveCostBasis(costBasis);
  const quantity =
    effectiveCostBasis === TAKEOFF_COST_BASIS.count
      ? (result?.count ?? 0)
      : getMeasurementQuantity(result);
  return unitCost * quantity;
}

export function formatCurrency(value: number): string {
  return value.toFixed(2);
}

export function formatMeasurementTotal(
  measurementType: MeasurementType,
  value: number | null | undefined
): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return formatMeasurementValue(value, measurementType);
}

export function formatMeasurementTotalForExport(
  measurementType: MeasurementType,
  value: number | null | undefined
): string {
  if (value === null || value === undefined) {
    return "";
  }
  return formatMeasurementValue(value, measurementType);
}

export type MeasurementBreakdownRow = {
  measurementType: MeasurementType;
  value: number;
  count: number;
};

export type GroupedMeasurementBreakdownRow = {
  measurementType: MeasurementType;
  displayValue: string;
  count: number;
  sortValue: number;
  targets: MeasurementBreakdownTargetRef[];
};

const MEASUREMENT_TYPE_ORDER: Record<MeasurementType, number> = {
  [MEASUREMENT_TYPES.LF]: 0,
  [MEASUREMENT_TYPES.SQFT]: 1,
  [MEASUREMENT_TYPES.CF]: 2,
};

const PDF_EXPORT_SCHEDULE_COLUMN_PREFIX = "schedule:";

export function groupMeasurementBreakdownByDisplayValue(
  breakdown: MeasurementBreakdownRow[] | undefined,
  breakdownTargets: MeasurementBreakdownTargetRow[] | undefined
): GroupedMeasurementBreakdownRow[] {
  const grouped = new Map<string, GroupedMeasurementBreakdownRow>();
  for (const row of breakdown ?? []) {
    const displayValue = formatMeasurementValue(row.value, row.measurementType);
    const roundedSortValue = roundMeasurementValue(row.value, row.measurementType);
    const key = `${row.measurementType}:${displayValue}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += row.count;
      existing.sortValue = Math.max(existing.sortValue, roundedSortValue);
      continue;
    }
    grouped.set(key, {
      measurementType: row.measurementType,
      displayValue,
      count: row.count,
      sortValue: roundedSortValue,
      targets: [],
    });
  }

  for (const row of breakdownTargets ?? []) {
    const displayValue = formatMeasurementValue(row.value, row.measurementType);
    const roundedSortValue = roundMeasurementValue(row.value, row.measurementType);
    const key = `${row.measurementType}:${displayValue}`;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        measurementType: row.measurementType,
        displayValue,
        count: row.count,
        sortValue: roundedSortValue,
        targets: [...row.targets],
      });
      continue;
    }
    const existingTargetIds = new Set(existing.targets.map((target) => String(target.measurementId)));
    for (const target of row.targets) {
      const targetId = String(target.measurementId);
      if (existingTargetIds.has(targetId)) {
        continue;
      }
      existingTargetIds.add(targetId);
      existing.targets.push(target);
    }
    existing.sortValue = Math.max(existing.sortValue, roundedSortValue);
  }

  for (const row of grouped.values()) {
    row.targets.sort(
      (left, right) =>
        String(left.planDocumentId).localeCompare(String(right.planDocumentId)) ||
        left.pageNumber - right.pageNumber ||
        String(left.measurementId).localeCompare(String(right.measurementId))
    );
  }

  return Array.from(grouped.values()).sort(
    (left, right) =>
      MEASUREMENT_TYPE_ORDER[left.measurementType] - MEASUREMENT_TYPE_ORDER[right.measurementType] ||
      right.sortValue - left.sortValue
  );
}

export type DownloadWorkbookOptions = {
  isCountsPending: boolean;
  countRows: CountRow[] | undefined;
  isMeasurementTotalsInitialLoad: boolean;
  hasMeasurementTotalsError: boolean;
  selectedTagsForExport: TagRecord[];
  selectedResults: CountRow[];
  includeColumns: string[];
  reportMode: "executive_summary" | "full";
  includeCosts: boolean;
  linkedPreviewsByTagId: Map<string, LinkedSchedulePreviewSummary>;
  scheduleOverridesByTagId: Map<string, ScheduleOverride>;
};

function getScheduleCell(args: {
  tagId: string;
  column: string;
  linkedPreviewsByTagId: Map<string, LinkedSchedulePreviewSummary>;
  scheduleOverridesByTagId: Map<string, ScheduleOverride>;
}) {
  const overrideValue = args.scheduleOverridesByTagId.get(args.tagId)?.cells[args.column];
  if (overrideValue !== undefined) {
    return overrideValue;
  }
  return args.linkedPreviewsByTagId.get(args.tagId)?.previewCells[args.column] ?? "";
}

function getExportColumnLabel(column: string): string {
  return column.startsWith(PDF_EXPORT_SCHEDULE_COLUMN_PREFIX)
    ? column.slice(PDF_EXPORT_SCHEDULE_COLUMN_PREFIX.length)
    : column;
}

function formatExportColumnValue(args: {
  column: string;
  tagId: string;
  result: CountRow | undefined;
  linkedPreviewsByTagId: Map<string, LinkedSchedulePreviewSummary>;
  scheduleOverridesByTagId: Map<string, ScheduleOverride>;
}) {
  if (args.column === "Count") {
    return args.result ? String(args.result.count) : "";
  }
  if (args.column === MEASUREMENT_TYPES.LF) {
    return formatMeasurementTotalForExport(MEASUREMENT_TYPES.LF, args.result?.lf);
  }
  if (args.column === MEASUREMENT_TYPES.SQFT) {
    return formatMeasurementTotalForExport(MEASUREMENT_TYPES.SQFT, args.result?.sqft);
  }
  if (args.column === MEASUREMENT_TYPES.CF) {
    return formatMeasurementTotalForExport(MEASUREMENT_TYPES.CF, args.result?.cf);
  }
  return getScheduleCell({
    tagId: args.tagId,
    column: getExportColumnLabel(args.column),
    linkedPreviewsByTagId: args.linkedPreviewsByTagId,
    scheduleOverridesByTagId: args.scheduleOverridesByTagId,
  });
}

function buildTakeoffExportTable(args: DownloadWorkbookOptions): {
  headers: string[];
  rows: string[][];
  rowTypes: Array<"main" | "detail">;
} {
  if (args.isCountsPending || !args.countRows) {
    throw new Error("Tag counts are still loading");
  }
  if (args.isMeasurementTotalsInitialLoad) {
    throw new Error("Measurement totals are still loading");
  }
  if (args.hasMeasurementTotalsError) {
    throw new Error("Measurement totals are unavailable");
  }
  if (args.selectedTagsForExport.length === 0) {
    throw new Error("Select at least one tag to download");
  }

  const resultsByTagId = new Map(
    args.selectedResults.map((row) => [row.tagId, row] as const)
  );
  const selectedColumns = args.includeColumns.length > 0 ? args.includeColumns : ["Count"];
  const headers = [
    "Tag",
    ...selectedColumns.map(getExportColumnLabel),
    ...(args.includeCosts ? ["Pricing Basis", "Unit Cost", "Total Cost"] : []),
  ];
  const rows: string[][] = [];
  const rowTypes: Array<"main" | "detail"> = [];

  for (const tag of args.selectedTagsForExport) {
    const result = resultsByTagId.get(tag._id);
    const totalCost = getTotalCost(result, tag.unitCost, tag.costBasis);
    const row = [
      tag.rawValue,
      ...selectedColumns.map((column) =>
        formatExportColumnValue({
          column,
          tagId: String(tag._id),
          result,
          linkedPreviewsByTagId: args.linkedPreviewsByTagId,
          scheduleOverridesByTagId: args.scheduleOverridesByTagId,
        })
      ),
    ];
    if (args.includeCosts) {
      row.push(
        getEffectiveCostBasis(tag.costBasis) === TAKEOFF_COST_BASIS.count
          ? "Count"
          : "Measurement",
        tag.unitCost == null ? "" : formatCurrency(tag.unitCost),
        totalCost == null ? "" : formatCurrency(totalCost)
      );
    }
    rows.push(row);
    rowTypes.push("main");

    if (args.reportMode !== "full" || !result) {
      continue;
    }

    const breakdownRows = groupMeasurementBreakdownByDisplayValue(
      result.breakdown,
      result.breakdownTargets
    );
    for (const breakdownRow of breakdownRows) {
      rows.push([
        "",
        ...selectedColumns.map((column) => {
          if (column === "Count") {
            return String(breakdownRow.count);
          }
          return column === breakdownRow.measurementType ? breakdownRow.displayValue : "";
        }),
        ...(args.includeCosts ? ["", "", ""] : []),
      ]);
      rowTypes.push("detail");
    }
  }

  return { headers, rows, rowTypes };
}

export function downloadTakeoffCsv(args: DownloadWorkbookOptions & { filename: string }): string | null {
  let table: ReturnType<typeof buildTakeoffExportTable>;
  try {
    table = buildTakeoffExportTable(args);
  } catch (error) {
    return error instanceof Error ? error.message : "Failed to build CSV export";
  }

  const csvContent = toCSV(table.headers, table.rows);
  downloadFile(
    csvContent,
    args.filename,
    "text/csv;charset=utf-8;"
  );
  return null;
}
